import { i18n } from './../../../i18n';
import {
  AcoFormData,
  AdgroupFormData,
  CampaignFormData,
  CreateMode,
  FALL_BACK_TYPE_ENUM,
  FullNormalFormData,
  NormalCreative,
} from '@tt4b/ttam-enums';
import {
  useCreativeActionTypeIs,
  useCreativeAdgroupData,
  useStdAdgroupOptimizationGoal,
  useStdCreativeAdgroupIs,
  ValidationError,
  validateExternalUrl,
} from '@tt4b/ttam-hooks';
import { checkURL } from '@tt4b/ttam-utils';
import { Ref, computed } from '@vue/composition-api';
import { isEmpty } from 'lodash-es';

export function usePropsFallback(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  createMode: Ref<CreateMode>;
  currentAdgroupIdCreativeList: Ref<NormalCreative[]>;
  isDisabledCreative: Ref<boolean>;
}) {
  const { adgroupData, campaignData, creativeData, createMode, currentAdgroupIdCreativeList, isDisabledCreative } =
    props;
  const { isSupportFallback, isAco, isTraffic, isAppDownload } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isExistAdgroup, isEditType } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });
  const { existAdgroupFirstCreativeInfo } = useCreativeAdgroupData({
    currentAdgroupIdCreativeList,
  });
  const { isOptGoalLPV } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });
  const isShowFallbackType = computed(
    () => isTraffic.value && isAppDownload.value && Boolean(creativeData.value.isOpenUrl),
  );
  const disabledDeeplinkAndFallback = computed(() => {
    if (
      isSupportFallback.value &&
      isExistAdgroup.value &&
      !isAco.value &&
      !isEmpty(existAdgroupFirstCreativeInfo.value)
    ) {
      return true;
    }

    // ACO编辑场景也不能修改(ACO编辑 + 在已有ACO下新建)
    if (isAco.value && isEditType.value) {
      return true;
    }

    if (isOptGoalLPV.value) {
      return true;
    }

    return isDisabledCreative.value;
  });
  return {
    isShowFallbackType,
    disabledDeeplinkAndFallback,
  };
}

/**
 * FallbackType 校验规则
 */
export const useFallbackTypeValidator = () => {
  const fallbackTypeValidator = computed(() => [
    {
      validator: (rule, value, callback, formData: FullNormalFormData) => {
        if (!formData.fallbackType) {
          return callback(
            new ValidationError({
              message: i18n.t('module_adgroup_fallback_type_empty'),
              messageI18nKey: 'module_adgroup_fallback_type_empty',
            }),
          );
        }

        return callback();
      },
      trigger: 'blur',
    },
  ]);
  return { fallbackTypeValidator };
};

/**
 * fallback 落地页校验
 */
export const useFallbackExternalUrlValidator = () => {
  const fallbackExternalUrlValidator = computed(() => [
    {
      validator: async (rule, value, callback, formData: FullNormalFormData) => {
        if (formData.fallbackType === FALL_BACK_TYPE_ENUM.fallbackWeb) {
          if (!formData.externalUrl) {
            return callback(
              new ValidationError({
                message: i18n.t('module_adgroup_traffic_fallback_type_link_empty'),
                messageI18nKey: 'module_adgroup_traffic_fallback_type_link_empty',
              }),
            );
          }
          if (!checkURL(formData.externalUrl)) {
            return callback(
              new ValidationError({
                message: i18n.t('module_adgroup_traffic_fallback_type_link_wrong'),
                messageI18nKey: 'module_adgroup_traffic_fallback_type_link_wrong',
              }),
            );
          }

          const validateRes = await validateExternalUrl({ url: formData.externalUrl as string });
          const { invalid, message } = validateRes;
          if (invalid) {
            return callback(
              new ValidationError({
                message: i18n.t(message),
                messageI18nKey: message,
              }),
            );
          }
        }

        return callback();
      },
      trigger: 'blur',
    },
  ]);
  return { fallbackExternalUrlValidator };
};
